import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billingKeys, BILLING_STALE_TIME_MS } from '../../services/queryClient';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { RefreshCw } from 'lucide-react';
import EOENotReadyTab from '../../components/billing/tabs/EOENotReadyTab';
import EOEPaidTab from '../../components/billing/tabs/EOEPaidTab';
import EOEPendingApprovalTab from '../../components/billing/tabs/EOEPendingApprovalTab';
import EOEPendingPaymentTab from '../../components/billing/tabs/EOEPendingPaymentTab';
import EOEReadyTab from '../../components/billing/tabs/EOEReadyTab';
import EOEReadyToSendTab from '../../components/billing/tabs/EOEReadyToSendTab';
import EOERejectedCancelledTab from '../../components/billing/tabs/EOERejectedCancelledTab';
import EOEDeniedTab from '../../components/billing/tabs/EOEDeniedTab';
import { ClaimReadinessProvider } from '../../components/billing/ClaimReadinessProvider';
import RemittanceManager, {
  type RemittanceManagerHandle,
  RemittanceDetailDialog,
} from '../../components/billing/RemittanceManager';
import { formatTabCount } from '../../components/billing/formatTabCount';
import { TabCountPill } from '../../components/billing/TabCountPill';
import {
  EOEClaimsManagerNavigationContext,
  EOEClaimsManagerTabProvider,
  type BillingOptionItem,
} from '../../components/billing/EOEClaimsManagerContext';
import {
  EOE_CLAIM_TABS,
  type EoeClaimTabId,
} from '../../components/billing/eoeClaimsManagerTabs';
import { buildClaimsExportParams } from '../../components/billing/billingExportParams';
import ClaimWorksheetEditor from '../../components/billing/ClaimWorksheetEditor';
import { terminalWorksheetViewStatuses } from '../../components/billing/worksheet/worksheetUtils';
import {
  STATUS_LABELS,
  STATUS_VARIANTS,
} from '../../components/billing/remittanceLabels';
import { useBillingTableQuery } from '../../hooks/useBillingTableQuery';
import {
  getActiveBillingCycles,
  getBillingClaimWorksheet,
  getBillingCyclesMeta,
  getRefreshBillingCyclesStatus,
  refreshBillingCycles,
} from '../../services/api';
// Same direct-from-sdk.gen import the other download flow uses
// (OpenOasisReport.download): the barrel re-exports a curated subset and does
// not include the blob export route.
import { exportBillingCyclesBillingBillingCyclesExportGet as exportBillingCycles } from '../../services/api/sdk.gen';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import { IconTooltipButton } from '../../components/common/IconTooltipButton';
import {
  ExportMenuButton,
  UserFacingExportError,
  type ExportFormat,
} from '../../components/common/ExportMenuButton';
import { PermissionSlug } from '../../constants/featureFlags';
import type {
  ActiveBillingCycleItem,
  BillingCycleRemittanceLink,
  BillingCycleTabCounts,
  BillingCycleWorksheetRead,
  EoeClaimTabMetadata,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

const EMPTY_TAB_COUNTS: Required<BillingCycleTabCounts> = {
  not_ready: 0,
  ready: 0,
  pending_approval: 0,
  ready_to_send: 0,
  pending_payment: 0,
  paid: 0,
  rejected_cancelled: 0,
  denied: 0,
  correction_draft: 0,
};

interface EOEStatusTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const TAB_COMPONENTS: Record<
  EoeClaimTabId,
  React.ComponentType<EOEStatusTabProps>
> = {
  'not-ready': EOENotReadyTab,
  ready: EOEReadyTab,
  'pending-approval': EOEPendingApprovalTab,
  'ready-to-send': EOEReadyToSendTab,
  'pending-payment': EOEPendingPaymentTab,
  paid: EOEPaidTab,
  'rejected-cancelled': EOERejectedCancelledTab,
  denied: EOEDeniedTab,
};

const getCountText = (
  tabCounts: BillingCycleTabCounts | null,
  countKey: string
) => formatTabCount(tabCounts?.[countKey as keyof BillingCycleTabCounts]);

const VIEW_HEADINGS: Record<
  'claims' | 'remittances',
  { title: string; description: string }
> = {
  claims: {
    title: 'Episodic Claims',
    description:
      'Manage and track episodic claims through the billing lifecycle',
  },
  remittances: {
    title: 'Remittances',
    description: '835 remittance advices received or uploaded for this agency',
  },
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;
const TERMINAL_FAILURES = ['FAILED', 'TERMINATED', 'CANCELED', 'TIMED_OUT'];

/**
 * EMR-2125: short chooser shown when a claim links to more than one live
 * remittance advice. One row per advice, newest check date first (the API
 * orders the links); picking one opens the remittance detail directly.
 */
const RemittanceChooserDialog: React.FC<{
  links: BillingCycleRemittanceLink[] | null;
  onPick: (remittanceAdviceId: string) => void;
  onClose: () => void;
}> = ({ links, onPick, onClose }) => (
  <Dialog open={links != null} onOpenChange={open => !open && onClose()}>
    <DialogContent className='max-w-md'>
      <DialogHeader>
        <DialogTitle>View remittance</DialogTitle>
        <DialogDescription>
          This claim is matched to more than one remittance. Pick one to open.
        </DialogDescription>
      </DialogHeader>
      <div className='flex flex-col gap-2'>
        {(links ?? []).map(link => (
          <Button
            key={link.remittance_advice_id}
            type='button'
            variant='outline'
            className='flex w-full items-center justify-between gap-2 whitespace-nowrap'
            onClick={() => onPick(link.remittance_advice_id)}
          >
            <span>{formatDateToMMDDYYYY(link.check_date ?? null)}</span>
            <span className='min-w-0 truncate text-muted-foreground'>
              {link.trace_number ?? '—'}
            </span>
            <span>{formatCurrencyOrDash(link.amount)}</span>
            <Badge
              variant='outline'
              className={`whitespace-nowrap text-xs ${STATUS_VARIANTS[link.status]}`}
            >
              {STATUS_LABELS[link.status]}
            </Badge>
          </Button>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

const formatCurrencyOrDash = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  return Number.isNaN(num)
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(num);
};

const EOEClaimsManager: React.FC = () => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const agencyId = useStore(state => state.user?.agency?.id);
  // Page-wide (not per-tab) and read here rather than passed down: the tabs
  // already read the same slice through useBillingTableQuery, which adds both
  // population filters to every list request. The toolbar checkboxes own the
  // setters.
  const hideTestPatients = useStore(state => state.hideTestPatients);
  const showInactiveEpisodes = useStore(state => state.showInactiveEpisodes);
  const hideImportedPatients = useStore(state => state.hideImportedPatients);
  const canExportClaims = useStore(state =>
    state.hasPermission(PermissionSlug.BILLING_EXPORT_CLAIMS)
  );
  const defaultTabId = EOE_CLAIM_TABS[0].id;
  const [activeView, setActiveView] = useState<'claims' | 'remittances'>(
    'claims'
  );
  const [activeTab, setActiveTab] = useState<EoeClaimTabId>(defaultTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<EoeClaimTabId>>(
    () => new Set([defaultTabId])
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  // EMR-2125 cross-tab navigation state. The remittance dialog renders at
  // page level (outside the view Tabs) so a claim-tab link can open it without
  // the remittances view mounting first; the chooser holds >1 linked advices.
  const [remittanceDialogRemittanceId, setRemittanceDialogRemittanceId] =
    useState<string | null>(null);
  const [remittanceChooserLinks, setRemittanceChooserLinks] = useState<
    BillingCycleRemittanceLink[] | null
  >(null);
  const [pendingClaim, setPendingClaim] = useState<{
    billingCycle: ActiveBillingCycleItem;
    claimId: string | null;
    initialWorksheet?: BillingCycleWorksheetRead;
  } | null>(null);
  const remittanceRef = useRef<RemittanceManagerHandle>(null);
  const isMountedRef = useRef(false);
  // EMR-2125: claim navigation request token. Each openClaimWorksheet call
  // takes the next number; a superseded or dialog-closed navigation's late
  // response carries a stale token and is ignored instead of replacing the
  // newer worksheet or reopening the dialog after the user closed it.
  const claimNavTokenRef = useRef(0);
  const queryClient = useQueryClient();

  // No prop hop: this reads the same billingUiSlice state the active tab
  // component reads, so the export carries exactly the filters the user sees.
  // Calling the hook a second time has no side effect — it only reads store
  // state and owns its own local pageIndex; the tabs' instances are untouched.
  // The second instance must map column ids to backend sort keys the same way
  // its tab does (billing_period → start_date on draft tabs,
  // billing_period_start elsewhere), or the export would send the raw column
  // id and silently fall back to end_date DESC.
  const claimsQuery = useBillingTableQuery(activeTab, {
    sortKeyByColumnId:
      activeTab === 'not-ready' || activeTab === 'ready'
        ? { billing_period: 'start_date' }
        : { billing_period: 'billing_period_start' },
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Supplies the tab list, the tab counts, and the agency-wide insurance/clinic
  // option lists for the toolbar dropdowns. The request-id guard this replaced
  // is now structural — one cache entry, so there is nothing to race.
  const metaQuery = useQuery({
    queryKey: billingKeys.billingMeta({
      hideTestPatients,
      showInactiveEpisodes,
      hideImportedPatients,
    }),
    queryFn: async () => {
      const response = await getBillingCyclesMeta({
        query: {
          hide_test_patients: hideTestPatients,
          show_inactive_episodes: showInactiveEpisodes,
          hide_imported_patients: hideImportedPatients,
        },
      });
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    staleTime: BILLING_STALE_TIME_MS,
    // Toasted once per failed fetch by the shared handler in queryClient.ts,
    // rather than from an effect that would re-fire on every remount.
    meta: { errorToast: { title: 'Failed to load billing cycle metadata' } },
  });

  // Every derived value below is gated on this rather than on `metaQuery.data`
  // alone: a failed *refetch* leaves the last successful payload in the cache,
  // so reading `data` directly would show the counts blank while the dropdowns
  // still offered options from a load that has since failed.
  const meta = metaQuery.isError ? undefined : metaQuery.data;

  // Backend-provided tabs when available, otherwise the hardcoded fallback.
  const tabs = useMemo<EoeClaimTabMetadata[]>(
    () => meta?.tabs ?? ([...EOE_CLAIM_TABS] as EoeClaimTabMetadata[]),
    [meta]
  );

  const tabCounts = useMemo<BillingCycleTabCounts | null>(
    () => (meta ? { ...EMPTY_TAB_COUNTS, ...meta.tab_counts } : null),
    [meta]
  );

  const insurances = useMemo<BillingOptionItem[]>(
    () =>
      (meta?.insurances ?? []).map(i => ({
        id: String(i.id),
        name: i.name,
      })),
    [meta]
  );

  const clinics = useMemo<BillingOptionItem[]>(
    () =>
      (meta?.clinics ?? []).map(c => ({
        id: String(c.id),
        name: c.name,
      })),
    [meta]
  );

  /** Reload the tab counts and option lists — used after a mutation changes
   *  which tab a claim belongs to. */
  const invalidateMetadata = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: billingKeys.billingMeta(),
    });
  }, [queryClient]);

  const handleRefresh = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    setIsRefreshing(true);
    try {
      if (activeView === 'remittances') {
        // Remittances are ingested by upload/webhook — there is no billing-cycle
        // workflow to kick. "Refresh" here means re-read the current list (same
        // filters/sort/page) plus the payor options.
        await remittanceRef.current?.refresh();
        if (!isMountedRef.current) {
          return;
        }
        showSuccess('Remittances refreshed');
        return;
      }
      const startResponse = await refreshBillingCycles({});
      if (!isMountedRef.current) {
        return;
      }
      if (startResponse.error || !startResponse.data) {
        throw startResponse.error ?? new Error('No workflow id returned');
      }
      const workflowId = startResponse.data.workflow_id;
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!isMountedRef.current) {
          return;
        }
        const statusResponse = await getRefreshBillingCyclesStatus({
          path: { workflow_id: workflowId },
        });
        if (!isMountedRef.current) {
          return;
        }
        if (statusResponse.error || !statusResponse.data) {
          throw statusResponse.error ?? new Error('No status returned');
        }
        const { status } = statusResponse.data;
        if (status === 'COMPLETED') {
          showSuccess('Billing cycles refreshed');
          // Invalidation rather than the old remount-by-key: every mounted tab
          // refetches in place, so the user's row selection survives a refresh
          // instead of being destroyed along with the component.
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: billingKeys.billingCycles(),
            }),
            invalidateMetadata(),
          ]);
          return;
        }
        if (TERMINAL_FAILURES.includes(status)) {
          throw new Error(`Refresh workflow ended with status ${status}`);
        }
      }
      if (!isMountedRef.current) {
        return;
      }
      showSuccess(
        'Refresh is still running in the background — reload this page shortly to see the results'
      );
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      console.error('Failed to refresh', error);
      showError(
        activeView === 'remittances'
          ? 'Failed to refresh remittances'
          : 'Failed to refresh billing cycles'
      );
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [activeView, invalidateMetadata, queryClient, showError, showSuccess]);

  const handleViewChange = useCallback((value: string) => {
    setActiveView(value as 'claims' | 'remittances');
  }, []);

  const handleExportClaims = useCallback(
    async (format: ExportFormat) => {
      try {
        const response = await exportBillingCycles({
          // buildClaimsExportParams strips page/per_page — the export route
          // rejects unknown query params (reject_unknown_file_params) — and
          // exports all stages with the active tab's shared filters.
          query: buildClaimsExportParams(claimsQuery.toParams(), format),
          responseType: 'blob' as const,
        });
        if (response.error || !response.data) {
          throw new UserFacingExportError(
            await getUserFacingApiMessageAsync(
              {
                response: {
                  status: response.response?.status,
                  data: response.response?.data ?? response.error,
                },
              },
              'Unable to export billing claims. Please try again.'
            )
          );
        }
        downloadResponseAsFile(response as never, `billing-claims.${format}`);
      } catch (error) {
        console.error('Failed to export billing claims', error);
        if (error instanceof UserFacingExportError) throw error;
        throw new UserFacingExportError(
          await getUserFacingApiMessageAsync(
            error,
            'Unable to export billing claims. Please try again.'
          )
        );
      }
    },
    [claimsQuery]
  );

  const handleTabChange = useCallback((value: string) => {
    const id = value as EoeClaimTabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  // EMR-2125: one linked advice opens the detail directly; several open the
  // chooser. The page-level dialog stays open across view switches. An
  // empty list opens nothing (callers explain why there is nothing to open).
  const openRemittance = useCallback((links: BillingCycleRemittanceLink[]) => {
    if (links.length === 0) return;
    setPendingClaim(null);
    if (links.length === 1) {
      setRemittanceDialogRemittanceId(links[0].remittance_advice_id);
      return;
    }
    setRemittanceChooserLinks(links);
  }, []);

  // EMR-2125: "View claim" resolves the cycle row by id (statuses=[] disables
  // the status predicate; the cross-link may point at a cycle outside the
  // active tab), then the exact claim version's preserved worksheet.
  const openClaimWorksheet = useCallback(
    async (billingCycleId: string, claimId: string | null) => {
      try {
        const token = ++claimNavTokenRef.current;
        const resp = await getActiveBillingCycles({
          body: {
            statuses: [],
            billing_cycle_ids: [billingCycleId],
            // The cycle may be a test patient's, imported, or on an inactive
            // episode; the link is explicit user intent. Opt out of all default
            // population filters so a cross-linked cycle can always be opened.
            hide_test_patients: false,
            show_inactive_episodes: true,
            hide_imported_patients: false,
            page: 1,
            per_page: 1,
          },
        });
        if (token !== claimNavTokenRef.current) return;
        if (resp.error || !resp.data || resp.data.items.length === 0) {
          throw resp.error ?? new Error('Billing cycle not found');
        }
        const billingCycle = resp.data.items[0];
        let initialWorksheet: BillingCycleWorksheetRead | undefined;
        if (claimId) {
          const wsResp = await getBillingClaimWorksheet({
            path: { claim_id: claimId },
          });
          if (token !== claimNavTokenRef.current) return;
          if (wsResp.error || !wsResp.data) {
            throw wsResp.error ?? new Error('Claim worksheet not found');
          }
          initialWorksheet = wsResp.data;
        }
        setRemittanceDialogRemittanceId(null);
        setRemittanceChooserLinks(null);
        setActiveView('claims');
        setPendingClaim({ billingCycle, claimId, initialWorksheet });
      } catch (err) {
        console.error('Failed to open claim worksheet', err);
        showError('Unable to open claim worksheet');
      }
    },
    [showError]
  );

  // The Remittances tab's own detail dialog closes through this too, so a
  // late View claim response from it cannot switch views or open a
  // worksheet the user already dismissed (EMR-2125 review finding).
  const cancelClaimNavigation = useCallback(() => {
    claimNavTokenRef.current += 1;
  }, []);
  const navigation = useMemo(
    () => ({ openRemittance, cancelClaimNavigation }),
    [openRemittance, cancelClaimNavigation]
  );

  return (
    <div className='space-y-6'>
      {/* One readiness provider for both draft tabs, above the Claims/Remittances
          and draft TabsContent so a tab switch does not discard the other
          tab's current list. Keyed by agency so an agency change discards the
          prior lifetime before new children can read it. */}
      <ClaimReadinessProvider key={agencyId}>
        {/* EMR-2125: the Claims <-> Remittances cross-links resolve through this
        context; both views and the row actions read it. */}
        <EOEClaimsManagerNavigationContext.Provider value={navigation}>
          <Tabs
            value={activeView}
            onValueChange={handleViewChange}
            className='flex w-full flex-col gap-6'
            variant='purple'
          >
            <div className='flex flex-wrap items-start justify-between gap-4'>
              <div>
                <h1 className='text-2xl font-bold'>
                  {VIEW_HEADINGS[activeView].title}
                </h1>
                <p className='text-muted-foreground text-sm'>
                  {VIEW_HEADINGS[activeView].description}
                </p>
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <TabsList>
                  <TabsTrigger value='claims'>Claims</TabsTrigger>
                  <TabsTrigger value='remittances'>Remittances</TabsTrigger>
                </TabsList>
                <IconTooltipButton
                  icon={
                    <RefreshCw
                      className={isRefreshing ? 'animate-spin' : undefined}
                    />
                  }
                  label='Refresh'
                  onClick={() => void handleRefresh()}
                  disabled={isRefreshing}
                />
                {activeView === 'claims' && canExportClaims ? (
                  <ExportMenuButton onExport={handleExportClaims} />
                ) : null}
              </div>
            </div>

            <TabsContent value='claims' className='m-0'>
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className='flex w-full flex-col gap-6'
                variant='purple'
              >
                <TabsList className='grid h-auto min-h-8 w-full grid-cols-1 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[520px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8'>
                  {tabs.map(tab => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className='group min-h-8 whitespace-normal px-1 py-1.5 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none min-[520px]:px-2 sm:text-xs lg:text-[13px]'
                    >
                      <span className='inline-flex min-w-0 max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center [overflow-wrap:anywhere] sm:flex-nowrap'>
                        <span className='min-w-0 [overflow-wrap:anywhere] sm:whitespace-nowrap xl:whitespace-normal'>
                          {tab.label}
                        </span>
                        <TabCountPill
                          label={tab.label}
                          value={getCountText(tabCounts, tab.count_key)}
                        />
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <Card>
                  <CardContent className='py-6'>
                    <EOEClaimsManagerTabProvider
                      activeTabId={activeTab}
                      insurances={insurances}
                      clinics={clinics}
                    >
                      {tabs.map(tab => {
                        const TabComponent =
                          TAB_COMPONENTS[tab.id as EoeClaimTabId];
                        return (
                          <TabsContent
                            key={tab.id}
                            value={tab.id}
                            className='m-0'
                          >
                            {mountedTabIds.has(tab.id as EoeClaimTabId) ? (
                              <TabComponent
                                onCountsInvalidated={invalidateMetadata}
                              />
                            ) : null}
                          </TabsContent>
                        );
                      })}
                    </EOEClaimsManagerTabProvider>
                  </CardContent>
                </Card>
              </Tabs>
            </TabsContent>

            <TabsContent value='remittances' className='m-0'>
              <Card>
                <CardContent className='py-6'>
                  <RemittanceManager
                    ref={remittanceRef}
                    onOpenClaim={openClaimWorksheet}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          {/* EMR-2125: the cross-link dialogs live outside the view Tabs so they
         open no matter which view is mounted. */}
          <RemittanceDetailDialog
            remittanceId={remittanceDialogRemittanceId}
            onClose={() => {
              // A late openClaimWorksheet response must not reopen a worksheet the
              // user already dismissed; supersede any in-flight navigation.
              claimNavTokenRef.current += 1;
              setRemittanceDialogRemittanceId(null);
            }}
            onOpenClaim={openClaimWorksheet}
          />
          <RemittanceChooserDialog
            links={remittanceChooserLinks}
            onPick={remittanceAdviceId => {
              setRemittanceChooserLinks(null);
              setRemittanceDialogRemittanceId(remittanceAdviceId);
            }}
            onClose={() => setRemittanceChooserLinks(null)}
          />
          {pendingClaim && (
            <ClaimWorksheetEditor
              key={
                pendingClaim.claimId ??
                pendingClaim.billingCycle.billing_cycle_id
              }
              billingCycle={pendingClaim.billingCycle}
              readOnly={terminalWorksheetViewStatuses.has(
                pendingClaim.billingCycle.billing_cycle_status
              )}
              initialWorksheet={pendingClaim.initialWorksheet}
              onClose={() => setPendingClaim(null)}
            />
          )}
        </EOEClaimsManagerNavigationContext.Provider>
      </ClaimReadinessProvider>
    </div>
  );
};

export default EOEClaimsManager;
